/**
 * Error state (lean counterpart of web/src/components/feedback/ErrorState.tsx).
 *
 * Renders a typed, recoverable error view driven by the DIRECT-011 envelope:
 * when the thrown value is an ApiError its server message and request id are
 * shown; unknown values degrade to a generic message. Never displays stack
 * traces, tokens, or internal details.
 */
import { AlertTriangle } from '@/components/icons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/CopyButton';
import { isApiError } from '@/api/errors';

export interface ErrorStateProps {
  title?: string;
  /** Overrides the envelope message when provided. */
  description?: string;
  /** The thrown error. Unknown values degrade to a generic message. */
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  error,
  onRetry,
  retryLabel = 'Try again',
  className,
  ...rest
}: ErrorStateProps) {
  const apiError = isApiError(error) ? error : undefined;
  const message = description ?? apiError?.message ?? 'The admin API request failed.';

  return (
    <Alert variant="destructive" className={className} data-testid={rest['data-testid'] ?? 'error-state'}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p>{message}</p>
        {apiError?.requestId ? (
          <div className="mt-2 flex items-center gap-1 text-xs">
            <span className="font-mono">{apiError.requestId}</span>
            <CopyButton value={apiError.requestId} label="Copy request id" className="h-6 w-6" />
          </div>
        ) : null}
        {onRetry ? (
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={onRetry}>
              {retryLabel}
            </Button>
          </div>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
